import React, {useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import usePrevious from '~/hooks/usePrevious';
import {sleevePrintDiffImageryMsg} from '~/components/ProductNote';
import {Alert, IconButton, InfoCircleIcon} from '@customink/pigment-react';
import {NOTE_AUTO_TOGGLE_DURATION} from '~/constants/ProductConstants';
import {useDispatch, useSelector} from 'react-redux';
// selectors
import {
  getProductNote,
  getSelectedProductId,
  showSleevePrintRepImageryNote
} from '~/selectors/ProductSelectors';
// actions
import {
  productPrintArea,
  sleevePrintPriceInfo
} from '~/actions/AnalyticsActions';

import './index.scss';

const ProductNoteMobile = ({
  showProductNote = false,
  handleToggle = () => {}
}) => {
  const dispatch = useDispatch();
  const productId = useSelector(getSelectedProductId);
  const note = useSelector(getProductNote);
  const showImageryNote = useSelector(showSleevePrintRepImageryNote);

  const timeout = useRef(productId);
  const prevProductId = usePrevious(productId);

  const toggleVisible = useCallback(() => {
    clearTimeout(timeout.current);
    handleToggle();
  }, [handleToggle]);

  // if started closed and product has changed,
  // open alert and close after delay
  useEffect(() => {
    const setCloseDelay = () => {
      timeout.current = setTimeout(() => {
        toggleVisible();
      }, NOTE_AUTO_TOGGLE_DURATION);
    };
    if (!showProductNote && prevProductId && productId !== prevProductId) {
      handleToggle();
      setCloseDelay();
    }
  }, [productId, showProductNote, prevProductId, handleToggle, toggleVisible]);

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  const handleOnClick = () => {
    if (!showProductNote) {
      dispatch(productPrintArea());
    } else {
      dispatch(sleevePrintPriceInfo('ndx/sleeve/priceinfo/minimize'));
    }
    toggleVisible();
  };

  const getNote = () => {
    if (!note && showImageryNote) {
      return sleevePrintDiffImageryMsg();
    }
    return note;
  };

  const productNote = getNote();
  const showNote = !!productNote;

  const baseClass = 'ndx-ProductNoteMobile';
  const wrapperClasses = classnames(baseClass, {
    'is-closed': !showProductNote
  });
  const alertClasses = classnames(`${baseClass}-alert`, {
    'is-closed': !showProductNote || !showNote
  });
  const iconClasses = classnames(`${baseClass}-icon`, {
    'is-closed': showProductNote
  });

  if (!showNote) {
    return null;
  }

  return (
    <div className={wrapperClasses} data-testid="product-note-mobile">
      <Alert
        className={alertClasses}
        severity="info"
        onClose={handleOnClick}
        data-testid="product-note-mobile-alert">
        {productNote}
      </Alert>
      <IconButton
        className={iconClasses}
        fill
        onClick={handleOnClick}
        size="large"
        data-testid="product-note-mobile-iconbutton">
        <InfoCircleIcon sx={{color: '#0043CE'}} fontSize="inherit" />
      </IconButton>
    </div>
  );
};

export default ProductNoteMobile;

ProductNoteMobile.propTypes = {
  handleToggle: PropTypes.func,
  iconSelected: PropTypes.bool,
  showProductNote: PropTypes.bool
};
